import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { AfterSubmitMessage } from './AfterSubmitMessage';
import { ThumbsUpThumbsDown } from './ThumbsUpThumbsDown';
import { ModalFeedback } from './ModalFeedback';
import { useUserFeedback } from '../hooks/useUserFeedback';
import { dataModel } from '../dataModel';

export const ThumbsUpThumbsDownWithModal = ({
  anchorKey,
  anchorType,
  title,
  thumbsUpOrDownFeedbackLabels,
  skipFeedbackForm,
  reasons,
  feedbackFormLabels,
  customerInsights,
  handleFeedbackData,
  successMessage,
  thanksMessageHideTime,
  size,
  forceCloseForm,
}) => {
  const {
    feedbackSubmitted,
    handleSelectOption,
    selectedOptionReasons,
    showFeedbackForm,
    handleFeedbackFormClose,
    handleFeedbackFormSubmit,
  } = useUserFeedback({
    anchorKey,
    anchorType,
    skipFeedbackForm,
    reasons,
    customerInsights,
    handleFeedbackData,
    forceCloseForm,
  });

  return (
    feedbackSubmitted
      ? <AfterSubmitMessage message={successMessage} thanksMessageHideTime={thanksMessageHideTime} />
      : (
        <>
          <div className="sui-flex sui-items-center sui-gap-1">
            <Typography variant="body-base">{title}</Typography>{' '}
            <ThumbsUpThumbsDown
              onClick={handleSelectOption}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...thumbsUpOrDownFeedbackLabels}
            />
          </div>
          {selectedOptionReasons && (
            <ModalFeedback
              open={showFeedbackForm}
              reasons={selectedOptionReasons}
              onClose={handleFeedbackFormClose}
              onSubmit={handleFeedbackFormSubmit}
              buttonsBaseId={anchorKey}
              size={size}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...feedbackFormLabels}
            />
          )}
        </>
      )
  );
};

ThumbsUpThumbsDownWithModal.displayName = 'ThumbsUpThumbsDownWithModal';
ThumbsUpThumbsDownWithModal.dataModel = dataModel;

ThumbsUpThumbsDownWithModal.propTypes = {
  anchorKey: PropTypes.string,
  anchorType: PropTypes.string,
  title: PropTypes.string,
  successMessage: PropTypes.string,
  thanksMessageHideTime: PropTypes.number,
  thumbsUpOrDownFeedbackLabels: PropTypes.shape({
    thumbsUpAriaLabel: PropTypes.string,
    thumbsDownAriaLabel: PropTypes.string,
  }),
  skipFeedbackForm: PropTypes.bool,
  reasons: PropTypes.shape({
    POSITIVE: PropTypes.arrayOf(PropTypes.string),
    NEGATIVE: PropTypes.arrayOf(PropTypes.string),
  }),
  feedbackFormLabels: PropTypes.shape({
    title: PropTypes.string,
    textAreaPlaceholder: PropTypes.string,
    submitCTA: PropTypes.string,
  }),
  customerInsights: PropTypes.string,
  handleFeedbackData: PropTypes.func.isRequired,
  modelName: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  forceCloseForm: PropTypes.bool,
};

ThumbsUpThumbsDownWithModal.defaultProps = {
  title: 'Was this helpful?',
  successMessage: AfterSubmitMessage.defaultProps.message,
  thanksMessageHideTime: AfterSubmitMessage.defaultProps.thanksMessageHideTime,
  thumbsUpOrDownFeedbackLabels: {},
  skipFeedbackForm: false,
  reasons: {
    POSITIVE: [],
    NEGATIVE: [
      'Inaccurate',
      'Irrelevant',
      'Unsafe/Offensive',
      'Other',
    ],
  },
  feedbackFormLabels: {},
  anchorKey: null,
  anchorType: null,
  customerInsights: null,
  modelName: null,
  size: 'sm',
  forceCloseForm: false,
};
